import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { TranslateService, TranslatePipe, LangChangeEvent } from '@ngx-translate/core';
import { AppState } from '../../../app.service';
import { GlobalVariables } from '../../../../common/global-variables';
import { Service } from '../network.service';
import { JobConstants } from '../../../../common/utils/job-constants';
import { JobsNotifierService } from '../../../services/job-notifier.service';
import { RefreshSharedService } from '../../../refresh-shared-service';
import { Subscription } from 'rxjs/Rx';

@Component({
    selector: 'gateways',
    templateUrl: 'gateways.html',
    styles: [require('./gateways.scss')],
    providers: [
        Service
    ]
})
export class Gateways implements OnInit {
    public gateways: any[];
    public gatewayId: string;
    public selectedGateway: any;
    private showGateways: boolean = true;
    private showCreateModal: boolean;
    private showDeleteModal: boolean;
    private createModalAction: string;
    private isTranslationready: boolean = true;
    private crudResult: any[];
    private isBusy: boolean = false;
    private gatewayCreationStatus = {
        'CREATED' : 'CREATED',
        'IN_PROGRESS': 'IN_PROGRESS',
        'FAILED': 'FAILED',
        'CREATE_FAILED': 'CREATE_FAILED'
    };
    private subscribeHandler: any;
    private rsService: RefreshSharedService;
    private refreshSubscription: Subscription;
    private currentClassName: string = '';

    constructor(public appState: AppState,
                public service: Service,
                private translate: TranslateService,
                private jobNotifierService: JobsNotifierService,
                private router: Router,
                private route: ActivatedRoute,
                private rss: RefreshSharedService) {
        translate.use(GlobalVariables.LOCALE);
        this.gateways = [];
        this.gatewayId = '';
        this.showCreateModal = false;
        this.showDeleteModal = false;
        this.createModalAction = '';
        this.rsService = rss;
        this.currentClassName = 'Gateways';
    }

    ngOnInit() {
        this.refreshSubscription = this.rsService.getRefreshParam()
        .subscribe (
        item => {
            this.handleEmittedValues(item);
        });

        this.route.params.forEach((params: Params) => {
            this.gatewayId = params['id'];
            this.rsService.setParam({
                'loadedComponent': this.currentClassName,
                'detailPageIDName': params['id'],
                'shouldReloadInventoryTree': true
            });
            this.loadData();
        });
    }

    handleEmittedValues(item) {
        if (item.loadedComponent === this.currentClassName) {
            this.loadData();
            this.service.getAllExternalNetworks();
        }
    }

    ngOnDestroy() {
        if (this.subscribeHandler) {
            this.subscribeHandler.unsubscribe();
        }
        this.rsService.resetParam();
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
        }
    }

    loadData() {
        if (this.gatewayId) {
            this.showGateways = false;
            this.getGateway(this.gatewayId);
        } else {
            this.showGateways = true;
            this.getGatewaysList();
        }
    }

    onVdcClick(gateway) {
        let selectedGateway = gateway.data;
        this.router.navigate(['inventory/org/' + selectedGateway.orgId + '/vdc/' + selectedGateway.vdcId]);
    }

    onEditGateway(event, gateway) {
        this.selectedGateway = gateway.data;
        this.createModalAction = 'EDIT';
        this.showCreateModal = true;
    }

    onCreateGateway(event) {
        this.createModalAction = 'ADD';
        this.showCreateModal = true;
    }

    onDeleteGateway(event, gateway) {
        this.selectedGateway = gateway.data;
        this.showDeleteModal = true;
    }

    onDeleteSubmit() {
        this.showDeleteModal = false;
        this.subscribeHandler = this.service.deleteGateway(this.selectedGateway)
            .subscribe(
            (crudResult: any) => {
                this.crudResult = crudResult;
            },
            error => this.service.logError(error, 'create gateway error'),
            () => this.service.logOperationCompleted('create gateway Completed')
            );
    }

    getGateway(id) {
        // Todo: Replace this with actual filter API
        this.gateways = [];
        this.isBusy = true;
        this.subscribeHandler = this.service.getGatewaysList().subscribe(
            (gateways: any) => {
                let data = gateways.filter(item => item.objectId === id);
                this.gateways = this.formatGatewayData(data);
                this.isBusy = false;
            },
            err => this.isBusy = false
        );
    }

    getGatewaysList() {
        this.gateways = [];
        this.isBusy = true;
        this.subscribeHandler = this.service.getGatewaysList().subscribe(
            gateways => {
                this.gateways = this.formatGatewayData(gateways);
                this.isBusy = false;
            },
            err => this.isBusy = false
        );
    }

    formatGatewayData(data) {
        let gateways = [];
        for (let i = 0; i < data.length; i++) {
            let gateway = {
                id: '',
                name: '',
                description: '',
                vdcName: '',
                vdcId: '',
                config: '',
                status: [],
                highAvailability: '',
                ips: {},
                externalNetworks: [],
                uiEndpoint: '',
                canEdit: true,
                canDelete: true,
                data: {}
            };

            gateway.data = data[i];
            gateway.id = data[i].objectId;
            gateway.name = data[i].name;
            gateway.description = data[i].description;
            gateway.vdcName = data[i].vdcName;
            gateway.vdcId = data[i].vdcId;
            gateway.uiEndpoint = data[i].uiEndpoint;
            gateway.config = this.getConfiguration(data[i]);
            gateway.highAvailability = this.getAvailability(data[i]);
            gateway.status = this.getStatus(data[i]);
            gateway.externalNetworks = this.getExternalNetwork(data[i]);
            gateway.ips = this.getGatewayIP(data[i]);
            gateway.canEdit = this.isEditable(data[i].status);
            gateway.canDelete = this.isDeletable(data[i].status);
            gateways.push(gateway);
        }
    return gateways;
    }

    isEditable(status) {
        return status === this.gatewayCreationStatus.IN_PROGRESS
                    || status === this.gatewayCreationStatus.CREATE_FAILED;
    }

    isDeletable(status) {
        return status === this.gatewayCreationStatus.IN_PROGRESS;
    }

    getConfiguration(row) {
        return (row.configuration && row.configuration.gatewayBackingConfig ? row.configuration.gatewayBackingConfig : '-');
    }

    getAvailability(row) {
        return (row.configuration && row.configuration.haEnabled ? 'Activated' : 'Deactivated');
    }

    getStatus(row) {
        let iconShape;
        let statusMessage = row.status;
        switch (row.status) {
            case this.gatewayCreationStatus.CREATED:
                statusMessage = 'Success';
                iconShape = 'success';
                break;
            case  this.gatewayCreationStatus.IN_PROGRESS:
                statusMessage = 'Busy';
                iconShape = 'ban';
                break;
            case  this.gatewayCreationStatus.FAILED:
                statusMessage = 'Failed';
                iconShape = 'error';
                break;
            case  this.gatewayCreationStatus.CREATE_FAILED:
                statusMessage = 'Creation Failed';
                iconShape = 'error';
                break;
            default:
                // console.log('status for the ' + row.name + ' is ' + row.status);

        }
        return [statusMessage, iconShape];
    }

    getExternalNetwork(row) {
        let gateway = row;
        let externalNetworks = [];
        if ( row.configuration && row.configuration.gatewayInterfaces && row.configuration.gatewayInterfaces.gatewayInterface ) {
            row.configuration.gatewayInterfaces.gatewayInterface.map(network => {
                let extNetwork = {
                    name: '',
                    ipRange: []
                };
                if (network.interfaceType === 'uplink') {
                    let networkName = network.name;
                    if (networkName) {
                        extNetwork.name = networkName;
                        let subnetParticipations = network.subnetParticipation;
                        if (subnetParticipations.length > 0) {
                            subnetParticipations.map(subnetParticipation => {
                                if (subnetParticipation.ipRanges) {
                                    let ipAllocations = subnetParticipation.ipRanges.ipRange;
                                    if (ipAllocations.length > 0) {
                                        ipAllocations.map(ipRange => {
                                            if (ipRange.startAddress === ipRange.endAddress) {
                                                extNetwork.ipRange.push(ipRange.startAddress);
                                            } else {
                                                extNetwork.ipRange.push(ipRange.startAddress + '-' + ipRange.endAddress);
                                            }
                                        });
                                    }
                                }

                            });
                        }
                    }
                }
                externalNetworks.push(extNetwork);
            });
        }
        return externalNetworks;
    }

    getGatewayIP(row) {
        let gateway = row;
        let ips = [];
        let displayIps = {
            hasMore: false,
            showMore: false,
            ips: [],
            moreIps: []
        };
        if ( row.configuration && row.configuration.gatewayInterfaces && row.configuration.gatewayInterfaces.gatewayInterface ) {
            row.configuration.gatewayInterfaces.gatewayInterface.map(network => {
                if (network.interfaceType === 'uplink') {
                    let subnetParticipations = network.subnetParticipation;
                    if (subnetParticipations.length > 0) {
                        subnetParticipations.map(subnetParticipation => {
                            if (subnetParticipation.ipAddress) {
                                ips.push(subnetParticipation.ipAddress);
                            }
                        });
                    }
                }
            });
        }
        if (ips.length > 2) {
            displayIps.hasMore = true;
            displayIps.ips = ips.slice(0, 2);
            displayIps.moreIps = ips.slice(2);
        } else {
            displayIps.ips = ips;
        }
        return displayIps;
    }
}



// WEBPACK FOOTER //
// ./src/app/components/network/gateways/gateways.ts